import { memo } from 'react';
import { sameNodeContent } from '../../Services/GraphRenderState';
import { Node, NodeProps, useReactFlow } from "@xyflow/react";

import useFlowActivityMetadata from "../../Hooks/Metadata/useFlowActivityMetadata";
import { ActivityModel } from "../../Models/FlowModel";
import { FlowEdge, FlowNode } from "../../Models/GraphModel";
import { ActivityTypes } from "../ActivityIcon";
import { COMPACT_AGENT_SERVICE_SIZE, DEFAULT_BLOCK_HEIGHT, DEFAULT_BLOCK_WIDTH, isHiddenByParentContainer } from '../../Services/AgentServiceCompact';
import BaseNode from "./BaseNode";
import CompactAgentServiceIcon from './CompactAgentServiceIcon';
import ToolNodeHeader from './ToolNodeHeader';
import "./BlockNode.scss";

export const BLOCK_NODE_TYPE = "block";

const COLLAPSED_BLOCK_HEIGHT = 56;

function BlockNode({ data, selected, id, width, height }: NodeProps & { data: ActivityModel }) {
	const activityMetadata = useFlowActivityMetadata(data.Type);
	const { setNodes } = useReactFlow<FlowNode, FlowEdge>();
	const isPlainBlock = data.Type === ActivityTypes.BLOCK;
	const isBlockTool = data.Type === ActivityTypes.BLOCK_TOOL;
	const isLoop = data.Type === ActivityTypes.LOOP;
	const isCompact = data.Metadata?.compact === true && isBlockTool;
	const isCollapsed = data.Metadata?.collapsed === true || isCompact;

	const toggleCollapsed = (event: React.MouseEvent) => {
		event.stopPropagation();
		if (!isBlockTool) return;

		setNodes((nodes) => {
			const nextCollapsed = !isCollapsed;
			const descendantIds = collectDescendantIds(id, nodes);
			const updatedNodes = nodes.map((node) => {
				if (node.id === id) {
					const metadata = node.data.Metadata ?? {};
					const currentWidth = node.width ?? width ?? (metadata.width as number) ?? DEFAULT_BLOCK_WIDTH;
					const currentHeight = node.height ?? height ?? (metadata.height as number) ?? DEFAULT_BLOCK_HEIGHT;
					const expandedWidth = (metadata.expandedWidth as number) ?? currentWidth;
					const expandedHeight = (metadata.expandedHeight as number) ?? currentHeight;

					return {
						...node,
						width: nextCollapsed ? currentWidth : expandedWidth,
						height: nextCollapsed ? COLLAPSED_BLOCK_HEIGHT : expandedHeight,
						data: {
							...node.data,
							Metadata: {
								...metadata,
								collapsed: nextCollapsed,
								...(nextCollapsed
									? {
											expandedWidth: currentWidth,
											expandedHeight: currentHeight,
										}
									: {}),
							},
						},
					};
				}

				if (!descendantIds.has(node.id)) {
					return node;
				}

				return {
					...node,
					selected: nextCollapsed ? false : node.selected,
				};
			});

			const nodesById = new Map(updatedNodes.map((node) => [node.id, node]));
			return updatedNodes.map((node) =>
				descendantIds.has(node.id)
					? {
						...node,
						hidden: nextCollapsed || isHiddenByParentContainer(node, nodesById),
						}
					: node,
			);
		});
	};

	return (
		<BaseNode
			id={id}
			data={data}
			selected={selected}
			activityMetadata={activityMetadata}
			nodeClassName={`block-node${isCollapsed ? ' block-node--collapsed' : ''}${isCompact ? ' block-node--compact' : ''}`}
			resizable
			resizerVisible={!isCollapsed}
			width={width}
			height={height}
		>
			{isCompact ? <CompactAgentServiceIcon activity={data} activityMetadata={activityMetadata} /> : (
				<>
					<ToolNodeHeader
						activity={data}
						activityMetadata={activityMetadata}
						isCollapsed={isCollapsed}
						centerTitle={isLoop || isPlainBlock}
						onToggleCollapsed={isBlockTool ? toggleCollapsed : undefined}
					/>
					{!isCollapsed && <div className="block-node__content" />}
				</>
			)}
		</BaseNode>
	);
}

function collectDescendantIds(rootId: string, nodes: FlowNode[]): Set<string> {
	const ids = new Set<string>();
	const queue = nodes.filter((node) => node.parentId === rootId).map((node) => node.id);

	while (queue.length > 0) {
		const id = queue.pop()!;
		ids.add(id);
		queue.push(...nodes.filter((node) => node.parentId === id).map((node) => node.id));
	}

	return ids;
}

export function nodeFromBlock(model: ActivityModel, x: number = 0, y: number = 0): Node<ActivityModel> {
	const isCompact = model.Type === ActivityTypes.BLOCK_TOOL && model.Metadata?.compact === true;
	const isCollapsedBlockTool = model.Type === ActivityTypes.BLOCK_TOOL && model.Metadata?.collapsed === true;

	return {
		id: model.Id,
		type: BLOCK_NODE_TYPE,
		position: { x, y },
		data: model,
		width: isCompact ? COMPACT_AGENT_SERVICE_SIZE : (model.Metadata?.width as number) ?? DEFAULT_BLOCK_WIDTH,
		height: isCompact ? COMPACT_AGENT_SERVICE_SIZE : isCollapsedBlockTool ? COLLAPSED_BLOCK_HEIGHT : (model.Metadata?.height as number) ?? DEFAULT_BLOCK_HEIGHT,
	} as Node<ActivityModel>;
}

export default memo(BlockNode, sameNodeContent);
